import { lazy, Suspense, type ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, matchPath } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { ThemeProvider } from "next-themes";
import { ScrollToTop } from "@/components/ScrollToTop";
import { TrailingSlashRedirect } from "@/components/TrailingSlashRedirect";
import { RouteTracker } from "@/components/analytics/RouteTracker";
import { LanguageRouter, LanguageRedirect } from "@/components/LanguageRouter";
import { CookieConsent } from "@/components/CookieConsent";
import { loadBlogPost } from "@/utils/blogPostCache";
import { SITE, isSiteLanguage } from "@/site";
import { hasPage, type OptionalPage } from "@/site/routes";
import { FLAGSHIP } from "@/data/flagship";
import mergedPages from "@site/data/merged-pages.json";
import { SITE_PAGES } from "virtual:site-pages";

// Every page is its own chunk (the homepage too, so its ~155 KB resort index
// is not preloaded everywhere). A page loaded through preload() renders
// directly, without suspending: main.tsx preloads the current URL's page
// before mounting, so the prerendered HTML is never swapped for the spinner.
type PageModule = { default: ComponentType };
type LazyPage = ComponentType & { preload: () => Promise<unknown> };
function lazyPage(load: () => Promise<PageModule>): LazyPage {
  let loaded: PageModule | undefined;
  const preload = () => load().then((m) => (loaded = m));
  const Lazy = lazy(preload);
  const Page = () => (loaded ? <loaded.default /> : <Lazy />);
  return Object.assign(Page, { preload });
}

const HomeV2Page = lazyPage(() => import("./pages/HomeV2Page"));
const DashboardPage = lazyPage(() => import("./pages/DashboardPage"));
const WebcamsPage = lazyPage(() => import("./pages/WebcamsPage"));
const ResortWebcamsPage = lazyPage(() => import("./pages/ResortWebcamsPage"));
const StayGuidePage = lazyPage(() => import("./pages/StayGuidePage"));
const StayIndexPage = lazyPage(() => import("./pages/StayIndexPage"));
const SnowReportIndexPage = lazyPage(() => import("./pages/SnowReportIndexPage"));
const SnowReportPage = lazyPage(() => import("./pages/SnowReportPage"));
const MapPage = lazyPage(() => import("./pages/MapPage"));
const ResortDetailPage = lazyPage(() => import("./pages/ResortDetailPage"));
const ResortGroupPage = lazyPage(() => import("./pages/ResortGroupPage"));
const RacetrackDetailPage = lazyPage(() => import("./pages/RacetrackDetailPage"));
const AllResortsPage = lazyPage(() => import("./pages/AllResortsPage"));
const RankingsPage = lazyPage(() => import("./pages/RankingsPage"));
const BestResortsPage = lazyPage(() => import("./pages/BestResortsPage"));
const LiveCamerasPage = lazyPage(() => import("./pages/LiveCamerasPage"));
const SeasonCalendarPage = lazyPage(() => import("./pages/SeasonCalendarPage"));
const FlagshipPage = lazyPage(() => import("./pages/flagship/FlagshipPage"));
const FlagshipGuidePage = lazyPage(() => import("./pages/flagship/FlagshipGuidePage"));
const FlagshipFaqPage = lazyPage(() => import("./pages/flagship/FlagshipFaqPage"));
const DestinationGuidePage = lazyPage(() => import("./pages/DestinationGuidePage"));
const SnowForecastPage = lazyPage(() => import("./pages/SnowForecastPage"));
const MonthlySnowForecastPage = lazyPage(() => import("./pages/MonthlySnowForecastPage"));
const OpeningDatesPage = lazyPage(() => import("./pages/OpeningDatesPage"));
const WinterTrafficPage = lazyPage(() => import("./pages/WinterTrafficPage"));
const AboutPage = lazyPage(() => import("./pages/AboutPage"));
const FaqPage = lazyPage(() => import("./pages/FaqPage"));
const PressKitPage = lazyPage(() => import("./pages/PressKitPage"));
const ContactPage = lazyPage(() => import("./pages/ContactPage"));
const PrivacyPage = lazyPage(() => import("./pages/PrivacyPage"));
const TermsPage = lazyPage(() => import("./pages/TermsPage"));
const BlogPage = lazyPage(() => import("./pages/BlogPage"));
const BlogPostPage = lazyPage(() => import("./pages/BlogPostPage"));
const WorkWithUsPage = lazyPage(() => import("./pages/WorkWithUsPage"));
const DataCoveragePage = lazyPage(() => import("./pages/DataCoveragePage"));
const SlugResolverPage = lazyPage(() => import("./pages/SlugResolverPage"));
const DataAuditPage = lazyPage(() => import("./pages/internal/DataAuditPage"));
const LinkAuditPage = lazyPage(() => import("./pages/internal/LinkAuditPage"));
const SeoDashboardPage = lazyPage(() => import("./pages/internal/SeoDashboardPage"));
const DemoHomePage = lazyPage(() => import("./pages/DemoHomePage"));
const NotFound = lazyPage(() => import("./pages/NotFound"));

// Route segments that differ per site (SiteConfig.routes).
const BEST = SITE.routes.bestResorts;
const FLAG = SITE.routes.flagship?.slug ?? "";

/**
 * Pages under /:lang/, in route order. The third column names the optional
 * page (src/site/routes.ts) that must be on for the route to exist; pages a
 * site lacks are not routed at all. vite.config.ts parses this table (for
 * sitemap lastmod), so keep one entry per line.
 */
const LANG_ROUTE_TABLE: Array<[path: string, page: LazyPage, gate?: OptionalPage]> = [
  ["old", DashboardPage],
  ["webcams", WebcamsPage],
  ["webcams/:slug", ResortWebcamsPage],
  ["where-to-stay", StayIndexPage],
  ["where-to-stay/:slug", StayGuidePage],
  ["snow-report", SnowReportIndexPage],
  ["snow-report/:slug", SnowReportPage],
  ["map", MapPage],
  ["resort/:slug", ResortDetailPage],
  ["area/:groupId", ResortGroupPage],
  ["resorts", AllResortsPage],
  ["rankings", RankingsPage],
  [`${BEST}`, BestResortsPage, "bestResorts"],
  ["live-cameras", LiveCamerasPage],
  ["season-calendar", SeasonCalendarPage, "seasonCalendar"],
  ["opening-dates", OpeningDatesPage, "openingDates"],
  ["winter-traffic", WinterTrafficPage, "winterTraffic"],
  ["guide/:slug", DestinationGuidePage],
  ["snow-forecast", SnowForecastPage, "snowForecast"],
  ["monthly-snow-forecast", MonthlySnowForecastPage, "monthlySnowForecast"],
  [`${FLAG}`, FlagshipPage, "flagship"],
  [`${FLAG}/travel-guide`, FlagshipGuidePage, "flagship"],
  [`${FLAG}/faq`, FlagshipFaqPage, "flagship"],
  ["racetrack/:slug", RacetrackDetailPage, "racetracks"],
  ["blog", BlogPage],
  ["blog/:slug", BlogPostPage],
  ["faq", FaqPage],
  ["press-kit", PressKitPage],
  ["contact", ContactPage],
  ["about", AboutPage],
  ["privacy", PrivacyPage],
  ["terms", TermsPage],
  ["work-with-us", WorkWithUsPage],
  ["internal/data-coverage", DataCoveragePage],
  ["internal/slug-resolver", SlugResolverPage],
  ["internal/data-audit", DataAuditPage],
  ["internal/link-audit", LinkAuditPage],
  ["internal/seo-dashboard", SeoDashboardPage],
  ["demo", DemoHomePage],
];

const routeEnabled = (gate?: OptionalPage) => !gate || (hasPage(gate) && (gate !== "flagship" || FLAGSHIP !== null));

/** Site-only pages (SiteConfig.extraRoutes -> sites/<id>/pages/<page>.tsx). */
const EXTRA_ROUTES = SITE.extraRoutes
  .filter((route) => SITE_PAGES[route.page])
  .map((route) => ({ ...route, Page: lazyPage(SITE_PAGES[route.page]) }));

// A site page at the same path as a shared page replaces it (e.g. Korea's own /faq).
const EXTRA_PATHS = new Set(EXTRA_ROUTES.map((route) => route.path));

const LANG_ROUTES: Array<[path: string, page: LazyPage]> = [
  ...LANG_ROUTE_TABLE.filter(([path, , gate]) => routeEnabled(gate) && !EXTRA_PATHS.has(path)).map(([path, page]): [string, LazyPage] => [path, page]),
  ...EXTRA_ROUTES.map((route): [string, LazyPage] => [route.path, route.Page]),
];

/**
 * Pages folded into another page (sites/<id>/data/merged-pages.json). The host
 * 301s them (scripts/generate_redirects.cjs); in-app navigation lands here.
 */
const MERGED_PAGES = Object.entries((mergedPages as { pages: Record<string, string> }).pages).map(
  ([from, to]): [string, string] => [from.replace(/^\/+/, ""), `..${to}/`]
);

/** Unprefixed legacy addresses that redirect to the language-prefixed page. */
const UNPREFIXED_REDIRECTS = LANG_ROUTES.map(([path]) => path).filter(
  (path) => path !== "old" && path !== "demo" && !path.startsWith("internal/")
);


/**
 * Loads the page chunk for a URL (and a blog post's JSON) before React mounts.
 * Resolves even if loading fails; the lazy route then retries as usual.
 */
export function preloadRoute(pathname: string): Promise<unknown> {
  const lang = pathname.match(/^\/([a-z]{2})(\/.*)?$/);
  if (!lang || !isSiteLanguage(lang[1])) return Promise.resolve();
  const rest = "/" + (lang[2] ?? "").replace(/^\/+|\/+$/g, "");
  if (rest === "/") return HomeV2Page.preload().catch(() => undefined);
  const found = LANG_ROUTES.find(([path]) => matchPath({ path: "/" + path, end: true }, rest));
  if (!found) return Promise.resolve();
  const blogSlug = matchPath("/blog/:slug", rest)?.params.slug;
  return Promise.all([found[1].preload(), blogSlug && loadBlogPost(blogSlug)]).catch(() => undefined);
}

const queryClient = new QueryClient();

// Minimal route-transition fallback (initial load is covered by LoadingScreen)
const RouteFallback = () => (
  <div className="flex min-h-[50vh] items-center justify-center" aria-hidden="true">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <LanguageProvider>
          <CurrencyProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <TrailingSlashRedirect />
                <RouteTracker />
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    {/* Language-prefixed routes */}
                    <Route path="/:lang" element={<LanguageRouter />}>
                      <Route index element={<HomeV2Page />} />
                      {MERGED_PAGES.map(([from, to]) => (
                        <Route key={from} path={from} element={<Navigate to={to} replace />} />
                      ))}
                      {LANG_ROUTES.map(([path, Page]) => (
                        <Route key={path} path={path} element={<Page />} />
                      ))}
                      <Route path="all-resorts" element={<Navigate to="../resorts/" replace />} />
                      <Route path="*" element={<NotFound />} />
                    </Route>

                    {/* Legacy routes without language prefix - redirect to language-prefixed */}
                    <Route path="/" element={<LanguageRedirect />} />
                    {UNPREFIXED_REDIRECTS.filter((path) => !EXTRA_ROUTES.some((route) => route.path === path)).map((path) => (
                      <Route key={path} path={`/${path}`} element={<LanguageRedirect />} />
                    ))}
                    {EXTRA_ROUTES.map((route) => {
                      // Single-language site pages (e.g. Japan's /ja/ski-faq/) go straight to that language.
                      const only = route.languages?.length === 1 ? route.languages[0] : null;
                      return (
                        <Route
                          key={route.path}
                          path={`/${route.path}`}
                          element={only ? <Navigate to={`/${only}/${route.path}/`} replace /> : <LanguageRedirect />}
                        />
                      );
                    })}
                    <Route path="/all-resorts" element={<Navigate to="/resorts/" replace />} />
                    <Route path="/internal/data-coverage" element={<DataCoveragePage />} />
                    <Route path="/internal/slug-resolver" element={<SlugResolverPage />} />
                    <Route path="/internal/data-audit" element={<DataAuditPage />} />
                    <Route path="/internal/link-audit" element={<LinkAuditPage />} />
                    <Route path="/internal/seo-dashboard" element={<SeoDashboardPage />} />
                    <Route path="/demo" element={<DemoHomePage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <CookieConsent />
              </BrowserRouter>
            </TooltipProvider>
          </CurrencyProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
